import { useReactiveVar } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    PRODUCT_TYPE_ACTIVATION_ADMIN_AUTHOR_MESSAGE,
    PRODUCT_TYPE_ACTIVATION_REVIEWER_MESSAGE,
    PT_ACTIVATION_DIALOG_HEIGHT,
    PT_ACTIVATION_DIALOG_MAX_WIDTH,
    PT_ACTIVATION_DIALOG_WIDTH,
} from 'pages/components/constants'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(2, 0),
        },
        maxWidth: pxToRem(PT_ACTIVATION_DIALOG_MAX_WIDTH),
    },

    dialogWrapper: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            width: pxToRem(PT_ACTIVATION_DIALOG_WIDTH),
            height: pxToRem(PT_ACTIVATION_DIALOG_HEIGHT),
        },
    },
    closeProductTypeDialogActionText: {
        fontSize: pxToRem(18),
        color: '#1899D4',
        fontWeight: 500,
        cursor: 'pointer',
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            padding: theme.spacing(2.5, 0, 3.5),
        },
    },
    dialogContent: {
        padding: theme.spacing(1, 0),
        overflow: 'hidden',
        color: theme.palette.common.black,
        fontSize: pxToRem(14),
        borderTop: 'none',
        borderBottom: 'none',
        maxWidth: pxToRem(PT_ACTIVATION_DIALOG_MAX_WIDTH),
    },
}))

export interface ProductTypeDeactivationWarningProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose: VoidFunction
}

const ProductTypeDeactivationWarning = ({
    openDialog,
    handleDialogClose,
}: ProductTypeDeactivationWarningProps) => {
    const classes = useStyles()
    const currentUserInfo = useReactiveVar(userInfo)

    if (!currentUserInfo || currentUserInfo.isReviewer === undefined) {
        return null
    }

    const { isReviewer } = currentUserInfo

    return (
        <Dialog
            open={openDialog}
            data-testid="productTypeRemovalWarning"
            onClose={onEscKeyPressOnDialog(handleDialogClose)}
            className={classes.dialogWrapper}
        >
            <DialogTitle className={classes.dialogTitle}>
                Unable to Activate/Inactivate Product Type
            </DialogTitle>
            <DialogContent
                className={classes.dialogContent}
                data-testid="dialogContent"
            >
                {isReviewer
                    ? PRODUCT_TYPE_ACTIVATION_REVIEWER_MESSAGE
                    : PRODUCT_TYPE_ACTIVATION_ADMIN_AUTHOR_MESSAGE}
            </DialogContent>

            <DialogActions className={classes.dialogAction}>
                <Typography
                    onClick={handleDialogClose}
                    className={classes.closeProductTypeDialogActionText}
                    data-testid="closeButtonProductTypeWarning"
                >
                    Close
                </Typography>
            </DialogActions>
        </Dialog>
    )
}

export default ProductTypeDeactivationWarning
